/**
 * Word文档导出工具
 * 基于"大纲转word.html"核心方法封装
 * 支持课程大纲数据的Word文档生成
 * 支持多种数据结构自动适配
 */

import JSZip from 'jszip'
import { saveAs } from 'file-saver'

/**
 * 数据字段映射表
 * 支持不同页面传入的不同字段名
 */
export const FIELD_MAPPING = {
    // 课程名称
    courseTitle: ['courseTitle', 'course_name', '课程名称'],
    // 课程编号
    courseCode: ['courseCode', 'course_code', '编号', '课程编号'],
    // 专题
    topic: ['topic', 'subject', '专题'],
    // 课程简介
    courseIntroduce: ['courseIntroduce', 'course_introduce', '简介', '课程简介'],
    // 课程亮点/特色
    courseHighlights: ['courseSpecialty', 'course_highlights', '亮点', '特色亮点', 'course_specialty'],
    // 目标人群/适用层级
    acsuName: ['acsuName', 'targetUser', 'course_target', '目标人群', '适用层级'],
    // 课程时长
    courseDuration: ['courseDays', 'courseStyle', 'course_duration', '时长', '课程时长'],
    // 授课方式/教学方式
    teachingMethod: ['courseStyleDes', 'teachingMethod', 'course_style_des', '授课方式', '教学方式'],
    // 课程收益/预期成果
    benefits: ['courseExpectedOutcome', 'benefits', 'course_expected_outcome', '收益', '课程收益', '预期成果'],
    // 课程大纲
    outline: ['courseOutline', 'outline', 'course_outline', '大纲', '课程大纲']
}

/**
 * 从源数据中提取指定字段的值
 * @param {Object} sourceData 源数据对象
 * @param {Array} fieldNames 可能的字段名数组
 * @returns {*} 字段值，未找到返回空字符串
 */
function extractField(sourceData, fieldNames) {
    for (const fieldName of fieldNames) {
        if (sourceData[fieldName] !== undefined && sourceData[fieldName] !== null) {
            return sourceData[fieldName]
        }
    }
    return ''
}

/**
 * 标准化课程数据 - 根据传入的数据结构自动适配
 * @param {Object} courseData 原始课程数据
 * @returns {Object} 标准化后的课程数据
 */
export function normalizeCourseData(courseData) {
    return {
        courseTitle: extractField(courseData, FIELD_MAPPING.courseTitle),
        courseCode: extractField(courseData, FIELD_MAPPING.courseCode),
        topic: extractField(courseData, FIELD_MAPPING.topic),
        courseIntroduce: extractField(courseData, FIELD_MAPPING.courseIntroduce),
        courseHighlights: extractField(courseData, FIELD_MAPPING.courseHighlights),
        acsuName: extractField(courseData, FIELD_MAPPING.acsuName),
        courseDuration: extractField(courseData, FIELD_MAPPING.courseDuration),
        teachingMethod: extractField(courseData, FIELD_MAPPING.teachingMethod),
        benefits: extractField(courseData, FIELD_MAPPING.benefits),
        outline: extractField(courseData, FIELD_MAPPING.outline)
    }
}

/**
 * 解析课程收益
 * @param {string} text 课程收益文本
 * @returns {Array} 收益数组（6个元素）
 */
export function parseBenefits(text) {
    if (!text) {
        console.log('parseBenefits: 输入为空')
        return []
    }

    // console.log('parseBenefits 输入:', JSON.stringify(text))
    // console.log('parseBenefits 行数:', text.split('\n').length)

    const benefits = []
    const lines = text.split('\n').filter(l => l.trim())
    // console.log('parseBenefits 解析后行数:', lines.length, lines)

    for (const line of lines) {
        const trimmed = line.trim()
        // console.log('处理行:', trimmed)

        // 匹配格式1: (1)收益1、(1)收益1
        let match = trimmed.match(/^[（(](\d+)[）)](.+)$/)
        if (match) {
            const index = parseInt(match[1]) - 1
            const content = match[2].trim()
            if (index >= 0 && index < 6 && content) {
                benefits[index] = content
                // console.log(`  匹配格式1成功，索引${index}:`, content)
                continue
            }
        }

        // 匹配格式2: 1.收益1、1、收益1（数字开头，点号或顿号分隔）
        match = trimmed.match(/^(\d+)[.、](.+)$/)
        if (match) {
            const index = parseInt(match[1]) - 1
            const content = match[2].trim()
            if (index >= 0 && index < 6 && content) {
                benefits[index] = content
                // console.log(`  匹配格式2成功，索引${index}:`, content)
                continue
            }
        }

        // 匹配格式3: （1）收益1（全角括号，无点号）
        match = trimmed.match(/^[（](\d+)[）](.+)$/)
        if (match) {
            const index = parseInt(match[1]) - 1
            const content = match[2].trim()
            if (index >= 0 && index < 6 && content) {
                benefits[index] = content
                // console.log(`  匹配格式3成功，索引${index}:`, content)
                continue
            }
        }
    }

    // 如果按行分割没有结果，尝试在同一行中匹配多个收益（格式：(1)收益1(2)收益2 或 1.收益1 2.收益2 或 1.收益1。2.收益2）
    if (benefits.filter(b => b).length === 0) {
        // 匹配 (1)收益1(2)收益2 格式（括号连续）
        const regex1 = /[（(](\d+)[）)]([^（(]*?)(?=[（(](\d+)[）)]|$)/g
        let match
        while ((match = regex1.exec(text)) !== null) {
            const index = parseInt(match[1]) - 1
            const content = match[2].trim()
            if (index >= 0 && index < 6 && content) {
                benefits[index] = content
            }
        }

        // 匹配 1.收益1。2.收益2。格式（句号分隔，最常见格式）
        if (benefits.filter(b => b).length === 0) {
            const regex2 = /(\d+)[.]([^1-9。]*?)(?=\d+[.]|$)/g
            let match2
            while ((match2 = regex2.exec(text)) !== null) {
                const index = parseInt(match2[1]) - 1
                const content = match2[2].trim()
                if (index >= 0 && index < 6 && content) {
                    benefits[index] = content
                }
            }
        }

        // 匹配 1.收益1 2.收益2 格式（空格分隔）
        if (benefits.filter(b => b).length === 0) {
            const regex3 = /(\d+)[.]([^1-9]*?)(?=\d+[.]|$)/g
            let match3
            while ((match3 = regex3.exec(text)) !== null) {
                const index = parseInt(match3[1]) - 1
                const content = match3[2].trim()
                if (index >= 0 && index < 6 && content) {
                    benefits[index] = content
                }
            }
        }
    }

    // 过滤掉空值，只返回有效收益
    const result = benefits.filter(b => b)
    // console.log('parseBenefits 结果:', result)
    return result
}

/**
 * 获取收益数量（用于选择模板）
 * @param {string} text 收益文本
 * @returns {number} 收益数量
 */
export function getBenefitsCount(text) {
    const benefits = parseBenefits(text)
    return benefits.length || 3 // 默认3个
}

/**
 * 解析课程大纲
 * @param {string} text 大纲文本
 * @returns {Array} 大纲数组
 */
export function parseOutline(text) {
    if (!text) return []

    const lines = text.split('\n').filter(l => l.trim())
    const outline = []

    for (const line of lines) {
        const trimmed = line.trim()

        // 一级标题：一、二、三、四、五、六、七、八、九、十
        const level1Match = trimmed.match(/^([一二三四五六七八九十]+)、(.+)$/)
        if (level1Match) {
            outline.push({
                level: 1,
                num: level1Match[1],
                content: level1Match[2].trim()
            })
            continue
        }

        // 二级标题：（一）或(一) - 支持全角和半角括号
        const level2Match = trimmed.match(/^[（(]([一二三四五六七八九十]+)[）)](.+)$/)
        if (level2Match) {
            outline.push({
                level: 2,
                num: level2Match[1],
                content: level2Match[2].trim()
            })
            continue
        }

        // 三级标题：1. 2. 3. 等（支持1.或1、格式）
        const level3Match = trimmed.match(/^(\d+)[.、](.+)$/)
        if (level3Match) {
            outline.push({
                level: 3,
                num: level3Match[1],
                content: level3Match[2].trim()
            })
            continue
        }

        // 默认：如果没有匹配到任何级别，则默认为三级标题（无序号）
        if (trimmed) {
            outline.push({
                level: 3,
                num: '',
                content: trimmed
            })
        }
    }

    return outline
}

/**
 * 数字转中文
 * @param {number} n 数字
 * @returns {string} 中文数字
 */
export function chineseNum(n) {
    const nums = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
    if (n <= 10) return nums[n]
    if (n < 20) return '十' + (n % 10 === 0 ? '' : nums[n % 10])
    return nums[Math.floor(n / 10)] + '十' + (n % 10 === 0 ? '' : nums[n % 10])
}

/**
 * XML转义
 * @param {string} str 原始字符串
 * @returns {string} 转义后的字符串
 */
export function escapeXmlContent(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
}

/**
 * 在XML中替换文本（保持格式）
 * @param {string} xmlContent XML内容
 * @param {string} search 搜索文本
 * @param {string} replace 替换文本
 * @returns {string} 替换后的XML
 */
export function replaceInXml(xmlContent, search, replace) {
    const escapeXml = (str) => {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;')
    }
    const escapedReplace = escapeXml(replace)
    return xmlContent.split(search).join(escapedReplace)
}

/**
 * 查找包含指定文本的段落
 * @param {string} xml XML内容
 * @param {string} searchText 搜索文本
 * @returns {string|null} 段落内容
 */
export function findParagraphContaining(xml, searchText) {
    const searchIdx = xml.indexOf(searchText)
    if (searchIdx < 0) return null

    // 向前找最近的<w:p开始标记
    let startIdx = searchIdx
    while (startIdx > 0) {
        const substr = xml.substring(Math.max(0, startIdx - 10), startIdx + 5)
        if (substr.includes('<w:p ') || substr.includes('<w:p>')) {
            startIdx = xml.lastIndexOf('<w:p', startIdx)
            break
        }
        startIdx--
    }

    // 向后找对应的</w:p>结束标记
    let endIdx = xml.indexOf('</w:p>', searchIdx)
    if (endIdx > 0) {
        endIdx += '</w:p>'.length
    }

    if (startIdx >= 0 && endIdx > startIdx) {
        return xml.substring(startIdx, endIdx)
    }
    return null
}

/**
 * 解析课程亮点
 * @param {string} text 亮点文本
 * @returns {Array} 亮点数组
 */
export function parseHighlights(text) {
    if (!text) return []
    const highlights = []
    const lines = text.split('\n').filter(l => l.trim())
    for (const line of lines) {
        const cleaned = line.trim().replace(/^\d+[、.：:]\s*/, '')
        if (cleaned) {
            highlights.push(cleaned)
        }
    }
    return highlights
}

/**
 * 生成单个Word文档
 * @param {Object} courseData 课程数据（支持多种数据结构）
 * @param {JSZip} templateZip 模版ZIP对象
 * @param {Object} options 配置选项
 * @param {boolean} options.autoNormalize 是否自动标准化数据（默认true）
 * @param {number} options.index 当前文档索引（用于生成唯一文件名）
 * @returns {Promise<Object>} 生成结果 {success, blob, filename, error}
 */
export async function generateCourseWord(courseData, templateZip, options = {}) {
    const { index = 0 } = options
    const { autoNormalize = true } = options

    try {
        // 克隆模版
        const newZip = new JSZip()
        for (const [path, file] of Object.entries(templateZip.files)) {
            if (!file.dir) {
                const content = await file.async('arraybuffer')
                newZip.file(path, content)
            }
        }

        // 读取document.xml
        let documentXml = await newZip.file('word/document.xml').async('string')

        // 标准化数据（自动适配不同数据结构）
        const data = autoNormalize ? normalizeCourseData(courseData) : courseData

        // 获取数据字段
        const courseCode = data.courseCode || ''
        const topic = data.topic || ''
        const courseName = data.courseTitle || ''
        const courseIntro = data.courseIntroduce || ''
        const courseHighlights = data.courseHighlights || ''
        const acsuName = data.acsuName || ''
        const courseDuration = data.courseDuration || ''
        const teachingMethod = data.teachingMethod || ''
        const benefits = parseBenefits(data.benefits || '')
        const outlineText = data.outline || ''

        // 1. 替换专题
        documentXml = replaceInXml(documentXml, 'XXX专题', topic)

        // 2. 替换课程名称（封面）
        documentXml = replaceInXml(documentXml, 'XXX课程', courseName)

        // 3. 替换课程编号
        documentXml = replaceInXml(documentXml, 'A1-001', courseCode)

        // 4. 替换内页的课程名称
        documentXml = replaceInXml(documentXml, 'courseTitle', courseName)

        // 5. 替换目标人群
        documentXml = replaceInXml(documentXml, 'acsuName', acsuName)

        // 6. 替换课程时长
        documentXml = documentXml.split('>courseStyle<').join(`>${courseDuration}<`)

        // 7. 替换课程简介
        documentXml = replaceInXml(documentXml, 'courseIntroduce', courseIntro)

        // 8. 替换课程亮点
        const highlights = parseHighlights(courseHighlights)
        const specialtyParagraph = findParagraphContaining(documentXml, 'courseSpecialty')

        if (specialtyParagraph && highlights.length > 0) {
            const specialtyIdx = documentXml.indexOf(specialtyParagraph)
            let highlightsXml = ''
            for (let i = 0; i < highlights.length; i++) {
                let para = specialtyParagraph
                para = para.replace(/courseSpecialty/g, `亮点${i + 1}：${highlights[i]}`)
                highlightsXml += para
            }
            const specialtyEndIdx = specialtyIdx + specialtyParagraph.length
            documentXml = documentXml.substring(0, specialtyIdx) +
                highlightsXml +
                documentXml.substring(specialtyEndIdx)
        } else if (highlights.length > 0) {
            const allHighlights = highlights.map((h, i) => `亮点${i + 1}：${h}`).join('\n')
            documentXml = replaceInXml(documentXml, 'courseSpecialty', allHighlights)
        } else {
            documentXml = replaceInXml(documentXml, 'courseSpecialty', '')
        }

        // 兼容旧格式：替换模版中的亮点占位符
        for (let i = 0; i < highlights.length && i < 10; i++) {
            const placeholder = `亮点${i + 1}：课程亮点内容`
            documentXml = replaceInXml(documentXml, placeholder, `亮点${i + 1}：${highlights[i]}`)
        }
        documentXml = documentXml.replace(/亮点\d+：课程亮点内容。?/g, '')

        // 9. 替换授课方式
        documentXml = replaceInXml(documentXml, 'courseStyleDes', teachingMethod)

        // 10. 替换课程收益/预期成果（支持两种占位符格式，根据实际收益数量）
        // console.log('benefits 数组:', benefits, '长度:', benefits.length)
        for (let i = 0; i < benefits.length; i++) {
            const value = benefits[i] || ''
            // 格式1: courseExpectedOutcome1, courseExpectedOutcome2...
            const placeholder1 = `courseExpectedOutcome${i + 1}`
            documentXml = replaceInXml(documentXml, placeholder1, value)
            // 格式2: 课程收益1, 课程收益2...
            const placeholder2 = `课程收益${i + 1}`
            documentXml = replaceInXml(documentXml, placeholder2, value)
        }
        // 清理未替换的空收益占位符（1-6个）
        for (let i = benefits.length; i <= 6; i++) {
            documentXml = replaceInXml(documentXml, `courseExpectedOutcome${i}`, '')
            documentXml = replaceInXml(documentXml, `课程收益${i}`, '')
        }

        // 11. 解析并替换课程大纲
        const outline = parseOutline(outlineText)

        // 尝试多种方式查找模板段落
        let level1Template = findParagraphContaining(documentXml, '模块一：')
        if (!level1Template) {
            level1Template = findParagraphContaining(documentXml, '一级标题1及内容')
        }

        let level2Template = findParagraphContaining(documentXml, '>二级')
        if (!level2Template) {
            level2Template = findParagraphContaining(documentXml, '二级标题1及内容')
            if (!level2Template) {
                level2Template = findParagraphContaining(documentXml, '>>二级标题1及内容')
            }
        }

        let level3Template = findParagraphContaining(documentXml, '三级标题')
        if (!level3Template) {
            level3Template = findParagraphContaining(documentXml, '>>三级标题1及内容')
            if (!level3Template) {
                level3Template = findParagraphContaining(documentXml, '>>>三级标题1及内容')
            }
        }

        if (level1Template && outline.length > 0) {
            const outlineStartIdx = documentXml.indexOf(level1Template)

            // 找最后一个三级标题段落的结束位置
            let lastLevel3Pos = documentXml.lastIndexOf('三级标题')
            if (lastLevel3Pos < 0) {
                lastLevel3Pos = documentXml.lastIndexOf('>>三级标题')
            }
            if (lastLevel3Pos < 0) {
                lastLevel3Pos = documentXml.lastIndexOf('>>>三级标题')
            }
            let outlineEndIdx = documentXml.indexOf('</w:p>', lastLevel3Pos)
            if (outlineEndIdx > 0) {
                outlineEndIdx += '</w:p>'.length
            }

            // 生成新的大纲XML
            let newOutlineXml = ''

            for (const item of outline) {
                if (item.level === 1) {
                    let para = level1Template
                    para = para.replace(/模块[一二三四五六七八九十]+：/g, '')
                    para = para.replace(/一级标题\d+及内容/g, escapeXmlContent(`${item.num}、${item.content}`))
                    newOutlineXml += para
                } else if (item.level === 2) {
                    if (level2Template) {
                        let para = level2Template
                        para = para.replace(/&gt;&gt;/g, '')
                        para = para.replace(/&gt;/g, '')
                        para = para.replace(/>二级</g, '><')
                        para = para.replace(/>>二级标题</g, '><')
                        para = para.replace(/二级标题\d+及内容/g, escapeXmlContent(`（${item.num}）${item.content}`))
                        para = para.replace(/标题\d+及内容/g, escapeXmlContent(`（${item.num}）${item.content}`))
                        newOutlineXml += para
                    } else {
                        let para = level1Template
                        para = para.replace(/模块[一二三四五六七八九十]+：/g, '')
                        para = para.replace(/一级标题\d+及内容/g, escapeXmlContent(`（${item.num}）${item.content}`))
                        newOutlineXml += para
                    }
                } else if (item.level === 3) {
                    if (level3Template) {
                        let para = level3Template
                        para = para.replace(/&gt;&gt;&gt;/g, '')
                        para = para.replace(/&gt;&gt;/g, '')
                        para = para.replace(/&gt;/g, '')
                        para = para.replace(/>>>三级标题</g, '><')
                        para = para.replace(/>>三级标题</g, '><')
                        para = para.replace(/>三级标题</g, '><')
                        const content = item.num ? `${item.num}.${item.content}` : item.content
                        para = para.replace(/三级标题\d+及内容/g, escapeXmlContent(content))
                        para = para.replace(/\d+及内容/g, escapeXmlContent(content))
                        newOutlineXml += para
                    } else if (level2Template) {
                        let para = level2Template
                        para = para.replace(/&gt;&gt;/g, '')
                        para = para.replace(/&gt;/g, '')
                        para = para.replace(/>二级</g, '><')
                        para = para.replace(/>>二级标题</g, '><')
                        const content = item.num ? `${item.num}.${item.content}` : item.content
                        para = para.replace(/二级标题\d+及内容/g, escapeXmlContent(content))
                        para = para.replace(/标题\d+及内容/g, escapeXmlContent(content))
                        newOutlineXml += para
                    } else {
                        let para = level1Template
                        para = para.replace(/模块[一二三四五六七八九十]+：/g, '')
                        const content = item.num ? `${item.num}.${item.content}` : item.content
                        para = para.replace(/一级标题\d+及内容/g, escapeXmlContent(content))
                        newOutlineXml += para
                    }
                }
            }

            // 替换整个大纲区域
            if (outlineStartIdx >= 0 && outlineEndIdx > outlineStartIdx) {
                documentXml = documentXml.substring(0, outlineStartIdx) +
                    newOutlineXml +
                    documentXml.substring(outlineEndIdx)
            }
        } else {
            // 备用方案：直接替换文本
            let level1Index = 0
            for (const item of outline) {
                if (item.level === 1) {
                    level1Index++
                    const placeholder = `一级标题${level1Index}及内容`
                    const value = escapeXmlContent(`${item.num}、${item.content}`)
                    documentXml = documentXml.split(placeholder).join(value)
                }
            }
            // 清理未替换的占位符
            documentXml = documentXml.replace(/模块[一二三四五六七八九十]+：/g, '')
            documentXml = documentXml.replace(/一级标题\d+及内容/g, '')
            documentXml = documentXml.replace(/(&gt;|>)?二级标题?\d*及?内?容?/g, '')
            documentXml = documentXml.replace(/(&gt;&gt;|>>)?三级标题\d+及内容/g, '')
        }

        // 更新document.xml
        newZip.file('word/document.xml', documentXml)

        // 生成文件
        const blob = await newZip.generateAsync({
            type: 'blob',
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        })

        return {
            success: true,
            blob: blob,
            filename: `${courseName || '课程大纲'}_${index + 1}.docx`.replace(/\.\./g, '.')
        }
    } catch (error) {
        console.error('生成Word文档失败:', error)
        return {
            success: false,
            error: error.message
        }
    }
}

/**
 * 批量生成Word文档
 * @param {Array} courseList 课程数据列表
 * @param {JSZip} templateZip 模版ZIP对象
 * @param {Object} options 选项 {onProgress, autoNormalize}
 * @returns {Promise<Object>} 生成结果 {success, results, total, successCount}
 */
export async function generateBatchWord(courseList, templateZip, options = {}) {
    const { onProgress, autoNormalize = true } = options
    const results = []
    const total = courseList.length
    let successCount = 0

    for (let i = 0; i < total; i++) {
        if (onProgress) {
            onProgress({ current: i + 1, total, course: courseList[i] })
        }

        const result = await generateCourseWord(courseList[i], templateZip, { autoNormalize, index: i })
        results.push(result)
        if (result.success) {
            successCount++
        }
    }

    return { success: true, results, total, successCount }
}

/**
 * 下载单个Word文档
 * @param {Blob} blob Word文档Blob
 * @param {string} filename 文件名
 */
export function downloadWord(blob, filename = '课程大纲.docx') {
    saveAs(blob, filename)
}

/**
 * 批量打包下载Word文档（ZIP格式）
 * @param {Array} results 生成结果数组
 * @param {string} zipFilename ZIP文件名
 */
export async function downloadBatchWord(results, zipFilename = '课程文档.zip') {
    const zip = new JSZip()
    for (const result of results) {
        if (result.success) {
            zip.file(result.filename, result.blob)
        }
    }
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    saveAs(zipBlob, zipFilename)
}

/**
 * 加载Word模版文件
 * @param {File|Blob|ArrayBuffer} file 模版文件
 * @returns {Promise<JSZip>} 模版ZIP对象
 */
export async function loadTemplate(file) {
    const arrayBuffer = file instanceof ArrayBuffer ? file : await file.arrayBuffer()
    return await JSZip.loadAsync(arrayBuffer)
}

/**
 * 从文件名中提取亮点数量
 * @param {string} filename 文件名
 * @returns {number|null} 亮点数量，未匹配返回null
 */
export function extractHighlightCount(filename) {
    const match = filename.match(/最新模版-(\d+)个\.docx/i)
    return match ? parseInt(match[1]) : null
}

/**
 * 加载模板库（多个模板文件）
 * @param {FileList|Array} files 模板文件列表
 * @returns {Promise<Object>} 模板库对象 {count: JSZip}
 */
export async function loadTemplateLibrary(files) {
    const templateLibrary = {}
    const fileList = Array.from(files)

    for (const file of fileList) {
        try {
            const count = extractHighlightCount(file.name)
            if (count === null) {
                console.warn(`跳过文件（文件名格式不正确）: ${file.name}`)
                continue
            }

            const arrayBuffer = await file.arrayBuffer()
            const template = await JSZip.loadAsync(arrayBuffer)
            templateLibrary[count] = template
            // console.log(`模板加载成功: ${file.name} (${count}个亮点)`)
        } catch (error) {
            console.error(`模板加载失败: ${file.name} - ${error.message}`)
        }
    }

    const loadedCounts = Object.keys(templateLibrary).sort((a, b) => a - b)
    // console.log(`模板库加载完成，支持的亮点数: ${loadedCounts.join(', ')}`)
    return templateLibrary
}

/**
 * 根据亮点数量从模板库中选择合适的模板
 * @param {number} highlightCount 亮点数量
 * @param {Object} templateLibrary 模板库 {count: JSZip}
 * @returns {JSZip|null} 选中的模板ZIP对象
 */
export function selectTemplateByHighlights(highlightCount, templateLibrary) {
    if (!templateLibrary || Object.keys(templateLibrary).length === 0) {
        return null
    }

    // 精确匹配
    if (templateLibrary[highlightCount]) {
        return templateLibrary[highlightCount]
    }

    // 寻找最接近的模板
    const availableCounts = Object.keys(templateLibrary).map(Number).sort((a, b) => a - b)
    let closestCount = availableCounts[0]

    for (const count of availableCounts) {
        if (Math.abs(count - highlightCount) < Math.abs(closestCount - highlightCount)) {
            closestCount = count
        }
    }

    // console.log(`使用 ${closestCount} 个亮点的模板（数据需要 ${highlightCount} 个）`)
    return templateLibrary[closestCount]
}

/**
 * 解析课程亮点并返回数量
 * @param {string} text 亮点文本
 * @returns {number} 亮点数量
 */
export function getHighlightCount(text) {
    if (!text) return 3 // 默认3个

    const highlights = parseHighlights(text)
    return highlights.length || 3
}

// 预加载的模板库（应用启动时加载）
let preloadedTemplateLibrary = null

/**
 * 设置预加载的模板库
 * @param {Object} library 模板库对象
 */
export function setPreloadedTemplateLibrary(library) {
    preloadedTemplateLibrary = library
    // console.log('模板库已预加载，支持的亮点数:', Object.keys(library).join(', '))
}

/**
 * 获取预加载的模板库
 * @returns {Object|null} 模板库对象
 */
export function getPreloadedTemplateLibrary() {
    return preloadedTemplateLibrary
}

/**
 * 从服务器加载本地模板库
 * @param {string} templatePath 模板目录路径（相对于public目录）
 * @returns {Promise<Object>} 模板库对象
 */
export async function loadLocalTemplateLibrary(templatePath = '/templates') {
    const library = {}

    // 本地模板文件名列表（固定）
    const templateFiles = [
        '最新模版-1个.docx',
        '最新模版-2个.docx',
        '最新模版-3个.docx',
        '最新模版-4个.docx',
        '最新模版-5个.docx',
        '最新模版-6个.docx'
    ]

    for (const filename of templateFiles) {
        try {
            const url = `${templatePath}/${filename}`
            const response = await fetch(url)
            if (!response.ok) continue

            const arrayBuffer = await response.arrayBuffer()
            const count = extractHighlightCount(filename)
            if (count !== null) {
                const template = await JSZip.loadAsync(arrayBuffer)
                library[count] = template
                // console.log(`本地模板加载成功: ${filename} (${count}个亮点)`)
            }
        } catch (error) {
            console.warn(`本地模板加载失败: ${filename} - ${error.message}`)
        }
    }

    const loadedCounts = Object.keys(library).sort((a, b) => a - b)
    // console.log(`本地模板库加载完成，支持的亮点数: ${loadedCounts.join(', ')}`)

    // 设置为预加载模板库
    setPreloadedTemplateLibrary(library)

    return library
}

/**
 * 导出课程Word文档（主入口方法）
 * @param {Array} courseList 课程数据列表
 * @param {Object} options 配置选项
 * @param {File|Blob|JSZip} options.template 模版文件或ZIP对象
 * @param {Function} options.onProgress 进度回调
 * @param {Function} options.onComplete 完成回调
 * @param {Function} options.onError 错误回调
 * @returns {Promise<Object>} 导出结果
 */
export async function exportCourseWord(courseList, options = {}) {
    const {
        template,
        onProgress,
        onComplete,
        onError,
        autoNormalize = true
    } = options

    if (!courseList || courseList.length === 0) {
        const error = new Error('课程数据为空')
        if (onError) onError(error)
        return { success: false, error: error.message }
    }

    if (!template) {
        const error = new Error('请提供Word模版')
        if (onError) onError(error)
        return { success: false, error: error.message }
    }

    try {
        // 加载模版
        let templateZip
        if (template instanceof JSZip) {
            templateZip = template
        } else {
            templateZip = await loadTemplate(template)
        }

        // 批量生成
        const { results, total, successCount } = await generateBatchWord(courseList, templateZip, {
            onProgress,
            autoNormalize
        })

        // 下载处理
        if (successCount === 0) {
            throw new Error('所有文档生成失败')
        } else if (results.length === 1 && results[0].success) {
            // 单个文件直接下载
            downloadWord(results[0].blob, results[0].filename)
        } else {
            // 多个文件打包下载
            await downloadBatchWord(results)
        }

        if (onComplete) {
            onComplete({ results, total, successCount })
        }

        return { success: true, results, total, successCount }
    } catch (error) {
        console.error('导出Word失败:', error)
        if (onError) onError(error)
        return { success: false, error: error.message }
    }
}

/**
 * 便捷方法：导出单个课程Word文档
 * @param {Object} courseData 课程数据
 * @param {Object} options 配置选项
 */
export async function exportSingleWord(courseData, options = {}) {
    const { template, filename } = options

    if (!template) {
        throw new Error('请提供Word模版')
    }

    let templateZip
    if (template instanceof JSZip) {
        templateZip = template
    } else {
        templateZip = await loadTemplate(template)
    }

    const result = await generateCourseWord(courseData, templateZip)

    if (result.success) {
        downloadWord(result.blob, filename || `${courseData.courseTitle || '课程'}.docx`)
    }

    return result
}

// 默认导出所有方法
export default {
    // 数据适配
    FIELD_MAPPING,
    normalizeCourseData,
    extractField,
    // 解析工具
    parseBenefits,
    parseOutline,
    parseHighlights,
    chineseNum,
    getHighlightCount,
    // XML处理
    escapeXmlContent,
    replaceInXml,
    findParagraphContaining,
    // 核心功能
    generateCourseWord,
    generateBatchWord,
    loadTemplate,
    extractHighlightCount,
    loadTemplateLibrary,
    selectTemplateByHighlights,
    // 预加载模板
    setPreloadedTemplateLibrary,
    getPreloadedTemplateLibrary,
    loadLocalTemplateLibrary,
    // 下载功能
    downloadWord,
    downloadBatchWord,
    exportCourseWord,
    exportSingleWord
}
